import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { MDBIcon } from 'mdb-react-ui-kit';
import { NavLink } from 'react-router-dom';
import { SalesReportViewer } from './SalesReport';
import { ProductsReportViewer } from './ProductsReport';
import { ws } from '../../../service/ws';
import API from '../../../service/api';
import Lang from '../../../service/lang';
import { useWindowSize } from '../../../hooks/useWindowSize';
import { host } from '../../../service/host';

export const ReportPage = () => {
    const { id } = useParams();
    const { t } = useTranslation();
    const { innerWidth } = useWindowSize();

    const [report, setReport] = useState(null);
    const [newReports, setNewReports] = useState([]);
    const [requestedReports, setRequestedReports] = useState([]);
    const [timeUntilExpire, setTimeUntilExpire] = useState();

    const mobile = useMemo(() => innerWidth < 800, [innerWidth]);
    const source = useMemo(
        () =>
            report
                ? report.machine
                    ? 'machine'
                    : report.location
                      ? 'location'
                      : 'group'
                : '',
        [report],
    );

    useEffect(() => {
        const callback = (r) => {
            setNewReports((prev) => prev.concat(r));
        };
        const unlisten = ws.on('report', callback);

        const interval = setInterval(() => {
            setTimeUntilExpire((prev) =>
                typeof prev == 'number' ? prev - 1 : prev,
            );
        }, 1000);

        return () => {
            unlisten();
            clearInterval(interval);
        };
    }, [ws]);

    useEffect(() => {
        if (newReports.length > 0) {
            newReports.forEach((r) => {
                if (requestedReports.includes(r.id)) {
                    window.open(`/reports/${r.id}`, '_blank');
                    setRequestedReports((prev) =>
                        prev.filter((id) => id !== r.id),
                    );
                }
            });
            setNewReports([]);
        }
    }, [newReports, requestedReports]);

    useEffect(() => {
        API.GET(`/api/reports/${id}`)
            .then((r) => {
                setTimeUntilExpire(
                    Math.floor(
                        (new Date(r.xat).getTime() - new Date().getTime()) /
                            1000,
                    ),
                );
                setReport(r);
            })
            .catch((err) => {
                alert(err.message);
            });
    }, []);

    const onNewReportRequest = useCallback((id) => {
        setRequestedReports((req) => req.concat(id));
    }, []);

    if (!report) return null;
    return (
        <div
            className="d-flex flex-column w-100 h-100 overflow-hidden"
            style={{ background: '#eeeeee' }}
        >
            <nav
                className="navbar navbar-expand-lg navbar-light bg-light p-0 w-100"
                style={{ zIndex: 15 }}
            >
                <div className="container-fluid justify-content-between">
                    <NavLink
                        to={'/group/' + report.group?.id}
                        className="position-absolute nav-link d-flex align-items-center p-1 user-select-none cursor-pointer"
                        style={{ cursor: 'pointer' }}
                    >
                        <div className="d-flex align-items-center pe-1">
                            <img
                                src={`/logo.png`}
                                style={{ width: 28, height: 28 }}
                            />
                            <span
                                className="h5 fw-bold mb-0 ms-1"
                                style={{
                                    color: '#e89328',
                                    textShadow: '1px 1px 1px rgba(0,0,0,0.1)',
                                }}
                            >
                                {!mobile && host.getTitle()}
                            </span>
                        </div>
                    </NavLink>
                    <span
                        className="d-flex flex-row align-items-center justify-content-center w-100"
                        style={{
                            marginLeft: mobile ? 50 : 300,
                            marginRight: mobile ? 50 : 300,
                            height: 36,
                            overflow: 'hidden',
                        }}
                    >
                        <MDBIcon
                            icon={
                                {
                                    group: 'layer-group',
                                    location: 'location-dot',
                                    machine: 'server',
                                }[source]
                            }
                            fas
                            color="primary"
                            size="lg"
                            className="me-1"
                        />
                        <span
                            className="m-0"
                            style={{
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                                overflow: 'hidden',
                                lineHeight: '1.3em',
                                fontSize: mobile ? '1.15rem' : '1.2rem',
                                fontWeight: 400,
                            }}
                        >
                            {
                                {
                                    group: report.group?.Name,
                                    location: report.location?.Name,
                                    machine: report.machine?.name,
                                }[source]
                            }
                        </span>
                    </span>

                    <div
                        className="d-flex align-items-center position-absolute"
                        style={{ right: 10 }}
                    >
                        <MDBIcon
                            fas
                            color="secondary"
                            size="lg"
                            icon={
                                report.type == 'sales'
                                    ? 'chart-line'
                                    : 'shopping-cart'
                            }
                        />
                    </div>
                </div>
            </nav>

            <div className="d-flex flex-row w-100 justify-content-center">
                <nav
                    className={
                        'navbar navbar-expand-lg p-1 d-flex border-top justify-content-center align-items-center text-primary overflow-hidden' +
                        (mobile ? ' w-100 px-1' : ' px-3')
                    }
                    style={{
                        background: '#fc8c0322',
                        height: 28,
                        fontSize: mobile ? '0.8rem' : '',
                    }}
                >
                    <MDBIcon icon="calendar-days" className="me-1" />
                    <span className="" style={{ fontWeight: '0.55rem' }}>
                        {new Date(report.startDate).toLocaleString(
                            Lang.instance.getCurrentLocale().code,
                        )}
                    </span>
                    <MDBIcon icon="arrow-right-long" className="mx-1" />
                    <span className="">
                        {new Date(report.endDate).toLocaleString(
                            Lang.instance.getCurrentLocale().code,
                        )}
                    </span>
                </nav>
            </div>

            {report.type == 'sales' && (
                <SalesReportViewer
                    report={report}
                    mobile={mobile}
                    onNewReportRequest={onNewReportRequest}
                />
            )}
            {report.type == 'products' && (
                <ProductsReportViewer
                    report={report}
                    mobile={mobile}
                    onNewReportRequest={onNewReportRequest}
                />
            )}

            <span
                className="d-flex flex-row w-100 align-items-center"
                style={{ fontSize: '0.8rem' }}
            >
                <span>
                    <MDBIcon icon="clock" className="me-1" />
                </span>
                <span>{t('expire_in')}:&nbsp;</span>
                {timeUntilExpire > 0 && (
                    <span>
                        {Math.floor(timeUntilExpire / 60)
                            .toString()
                            .padStart(2, '0')}
                        :{(timeUntilExpire % 60).toString().padStart(2, '0')}
                    </span>
                )}
                {timeUntilExpire <= 0 && (
                    <span className="text-danger">{t('expired')}</span>
                )}
                <span className="d-flex flex-row flex-fill justify-content-end">
                    <span
                        className="fst-italic text-muted"
                        style={{ fontSize: '0.8rem' }}
                    >
                        {t('generated_in')}: {report.result.runtime}s
                    </span>
                </span>
            </span>
        </div>
    );
};
